<template>
  <div class="notification-bell-wrapper" v-if="!isHiddenRoute">
    <div class="col-auto" style="padding-right: 0px; position: relative">
      <div
        class="notification-wrapper"
        :class="{ 'has-new-notifications': unseenCount > 0 }"
        @click="toggleDropdown"
      >
        <div class="notification-icon">
          <Icon icon="mdi:bell" width="18" height="18" />
          <span v-if="unseenCount > 0" class="notification-badge">
            {{ unseenCount > 99 ? "99+" : unseenCount }}
          </span>
        </div>
      </div>

      <div v-if="showDropdown" class="notifications-dropdown" @click.stop>
        <div class="notifications-header">
          <h6 class="notifications-title">Notifications</h6>
          <span v-if="unseenCount > 0" class="unread-count-badge">{{ unseenCount }}</span>
        </div>

        <div class="notifications-filter">
          <div v-if="selectedContact" class="contact-filter-chip">
            <span class="contact-filter-chip__avatar">
              <img
                v-if="selectedContact.profilePictureUrl"
                :src="selectedContact.profilePictureUrl"
                :alt="getContactName(selectedContact)"
                class="contact-filter-chip__avatar-img"
              />
              <Icon v-else icon="mdi:account" width="12" height="12" />
            </span>
            <span class="contact-filter-chip__name">{{ getContactName(selectedContact) }}</span>
            <button
              class="contact-filter-chip__clear"
              aria-label="Clear contact filter"
              @click="clearContactFilter"
            >
              <Icon icon="mdi:close" width="14" height="14" />
            </button>
          </div>

          <div v-else ref="contactFilterAnchorRef" class="contact-filter-search-wrapper">
            <div class="contact-filter-search">
              <Icon icon="mdi:magnify" width="15" height="15" class="contact-filter-search__icon" />
              <input
                v-model="contactSearchTerm"
                class="contact-filter-search__input"
                placeholder="Filter by contact..."
              />
              <span v-if="isSearchingContacts" class="contact-filter-search__spinner" />
            </div>

            <Teleport to="body">
              <div
                v-if="debouncedContactSearch.length >= 2"
                class="contact-filter-results"
                :style="{
                  top: `${contactResultsPosition.top}px`,
                  left: `${contactResultsPosition.left}px`,
                  width: `${contactResultsPosition.width}px`,
                }"
                @click.stop
              >
                <div
                  v-if="!isSearchingContacts && contactSearchResults.length === 0"
                  class="contact-filter-empty"
                >
                  No contacts found
                </div>
                <button
                  v-for="result in contactSearchResults"
                  :key="result.id"
                  class="contact-filter-result-item"
                  @click="selectContactFilter(result)"
                >
                  <span class="contact-filter-result-item__avatar">
                    <img
                      v-if="result.profilePictureUrl"
                      :src="result.profilePictureUrl"
                      :alt="getContactName(result)"
                      class="contact-filter-result-item__avatar-img"
                    />
                    <template v-else>{{ getContactInitials(result) }}</template>
                  </span>
                  <span class="contact-filter-result-item__info">
                    <span class="contact-filter-result-item__name">{{
                      getContactName(result)
                    }}</span>
                    <span v-if="result.email" class="contact-filter-result-item__meta">{{
                      result.email
                    }}</span>
                  </span>
                </button>
              </div>
            </Teleport>
          </div>
        </div>

        <div v-if="isLoading" class="notifications-loading">
          <LoadingBars size="sm" />
        </div>

        <div v-else-if="notifications.length > 0" class="notifications-list">
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="notification-item"
            :class="{ unread: !notification.isSeen }"
            @click="handleNotificationClick(notification)"
          >
            <div class="notification-icon-wrapper">
              <div class="notification-type-icon" :class="getNotificationTypeClass(notification)">
                <Icon :icon="getNotificationIcon(notification)" width="20" height="20" />
              </div>
              <span v-if="!notification.isSeen" class="unread-indicator"></span>
            </div>
            <div class="notification-content">
              <div class="notification-message">
                <h6 class="notification-title" :class="{ 'unread-text': !notification.isSeen }">
                  {{ getNotificationTitle(notification) }}
                </h6>
                <p
                  class="notification-message-text"
                  :class="{ 'unread-text': !notification.isSeen }"
                >
                  {{ notification.message }}
                </p>
                <div v-if="notification.contact" class="notification-entity">
                  <Icon icon="mdi:account" width="14" height="14" />
                  <span>{{
                    [notification.contact.fname, notification.contact.lname]
                      .filter(Boolean)
                      .join(" ") || "Contact"
                  }}</span>
                </div>
                <div v-if="notification.meeting" class="notification-entity">
                  <Icon icon="mdi:calendar" width="14" height="14" />
                  <span>{{ notification.meeting.title }}</span>
                </div>
              </div>
              <div class="notification-footer">
                <small class="notification-time">
                  {{ formatNotificationTime(notification.createdAt) }}
                </small>
                <div class="notification-actions">
                  <button
                    v-if="isEngagementNotification(notification)"
                    class="notification-action-link"
                    @click.stop="handleViewEngagement(notification)"
                  >
                    View
                  </button>
                  <button
                    v-else-if="isBucketPromptNotification(notification)"
                    class="notification-action-link"
                    @click.stop="handleViewBucketPrompt(notification)"
                  >
                    View
                  </button>
                  <button
                    v-else-if="isCueCardsNotification(notification)"
                    class="notification-action-link"
                    @click.stop="handleViewCueCards(notification)"
                  >
                    View
                  </button>
                  <button
                    v-else-if="isMentionNotification(notification)"
                    class="notification-action-link"
                    @click.stop="handleViewMention(notification)"
                  >
                    View
                  </button>
                  <router-link
                    v-else-if="isTasksDueNotification(notification)"
                    :to="getTasksDueRoute(notification)"
                    class="notification-action-link"
                    @click.stop="showDropdown = false"
                  >
                    View
                  </router-link>
                  <router-link
                    v-else-if="notification.linkUrl && notification.type !== 'enrichment'"
                    :to="buildLinkRoute(notification.linkUrl, notification)"
                    class="notification-action-link"
                    @click.stop="showDropdown = false"
                  >
                    View
                  </router-link>
                  <router-link
                    v-else-if="
                      notification.type === 'enrichment' && notification.meta?.contactIds?.length
                    "
                    :to="getEnrichmentViewRoute(notification)"
                    class="notification-action-link"
                    @click.stop="showDropdown = false"
                  >
                    View
                  </router-link>
                  <router-link
                    v-else-if="notification.contactId"
                    :to="{ name: CONTACT_OVERVIEW_ROUTE, params: { id: notification.contactId } }"
                    class="notification-action-link"
                    @click.stop="showDropdown = false"
                  >
                    View
                  </router-link>
                  <router-link
                    v-else-if="notification.meetingId"
                    :to="{
                      name: MEETINGS_ROUTE,
                      query: { status: 'all', toOpenMeetingId: notification.meetingId },
                    }"
                    class="notification-action-link"
                    @click.stop="showDropdown = false"
                  >
                    View
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <div v-if="hasNextPage" class="load-more-wrapper">
            <button class="load-more-btn" :disabled="isFetchingNextPage" @click.stop="loadMore">
              <Icon
                v-if="isFetchingNextPage"
                icon="mdi:loading"
                class="spin"
                width="14"
                height="14"
              />
              <span>{{ isFetchingNextPage ? "Loading..." : "Show older notifications" }}</span>
            </button>
          </div>
        </div>

        <div v-else class="no-notifications">
          <Icon icon="mdi:bell-off-outline" width="36" height="36" />
          <p class="text-center">No notifications</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useToast } from "@/composables/ui/useToast";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import {
  BUCKETS_LIST_ROUTE,
  BUCKETS_ROUTE,
  CONTACT_OVERVIEW_ROUTE,
  CONTACTS_ROUTE,
  MEETINGS_ROUTE,
  SUBSCRIPTION_SUCCESS_ROUTE,
  SUBSCRIPTION_WALL_ROUTE,
} from "@/router/routes";
import { useNotifications } from "@/composables/notifications/useNotifications";
import { useContacts } from "@/composables/contacts/useContacts";
import { useMarkNotificationSeen } from "@/composables/notifications/useMarkNotificationSeen";
import { useGlobalFollowupModal } from "@/composables/bucket/useGlobalFollowupModal";
import { NotificationCategory } from "@/utils/enums";
import filters from "@/utils/filters";
import debounce from "lodash/debounce";
import type { ContactSearchResult } from "@/dtos/Contact.dto";

const formatNotificationTime = (value: string | undefined) => filters.getDateAndTime(value ?? "");

interface Props {
  route: string;
  account: {
    id: string;
    organizationId: string;
  };
}

const props = defineProps<Props>();

const $toast = useToast();
const { openFollowupModal } = useGlobalFollowupModal();
const router = useRouter();
const route = useRoute();

const ENGAGEMENT_EVENT_TO_TASK_TYPE: Record<string, string> = {
  email_opened: "email",
  email_clicked: "email",
  linkedin_connection: "linkedIn",
  inmail_read: "linkedIn",
  linkedin_message_read: "linkedIn",
  whatsapp_message_read: "whatsApp",
  sms_received: "sms",
  call_conversation: "call",
  linkedin_message: "linkedIn",
  whatsapp_message: "whatsApp",
  email_received: "email",
};

const isEngagementNotification = (n: any) =>
  !!n.contactId && !!n.meta?.eventType && n.meta.eventType in ENGAGEMENT_EVENT_TO_TASK_TYPE;

const isBucketPromptNotification = (n: any) =>
  n.type === NotificationCategory.task &&
  !!n.contactId &&
  !n.meta?.eventType &&
  n.message?.toLowerCase().includes("call with");

const isCueCardsNotification = (n: any) =>
  n.type === NotificationCategory.ai_generation && !!n.contactId;

const isMentionNotification = (n: any) => n.type === NotificationCategory.mention && !!n.contactId;

const isTasksDueNotification = (n: any) => {
  const message = n?.message?.toLowerCase() || "";
  return (
    n.type === NotificationCategory.task &&
    !n.meta?.eventType &&
    message.includes("tasks from") &&
    message.includes("buckets")
  );
};

const getTasksDueRoute = (notification: any) => {
  const bucketSlug = notification.meta?.bucketOptionSlug;
  if (bucketSlug) {
    return { name: BUCKETS_ROUTE, params: { bucket: bucketSlug }, query: { status: "due" } };
  }
  return { name: BUCKETS_LIST_ROUTE };
};

const handleViewEngagement = (notification: any) => {
  const taskType = ENGAGEMENT_EVENT_TO_TASK_TYPE[notification.meta?.eventType] || "call";

  openFollowupModal({
    contactId: notification.contactId,
    contactFname: notification.contact?.fname,
    contactLname: notification.contact?.lname,
    taskType,
    emailThreadId: notification.meta?.threadId || notification.meta?.emailId,
    chatId: notification.meta?.chatId,
  });
  closeDropdown();
};

const handleViewBucketPrompt = (notification: any) => {
  openFollowupModal({
    contactId: notification.contactId,
    contactFname: notification.contact?.fname,
    contactLname: notification.contact?.lname,
    taskType: "call",
  });
  closeDropdown();
};

const handleViewCueCards = (notification: any) => {
  openFollowupModal({
    contactId: notification.contactId,
    contactFname: notification.contact?.fname,
    contactLname: notification.contact?.lname,
    taskType: "email",
  });
  closeDropdown();
};

const handleViewMention = (notification: any) => {
  openFollowupModal({
    contactId: notification.contactId,
    contactFname: notification.contact?.fname,
    contactLname: notification.contact?.lname,
    taskType: "chat",
  });
  closeDropdown();
};

const showDropdown = ref(false);
const lastNotificationTimestamp = ref<number | null>(null);

const hideForRoutes = [SUBSCRIPTION_WALL_ROUTE, SUBSCRIPTION_SUCCESS_ROUTE];
const isHiddenRoute = computed(() => hideForRoutes.includes(props.route));

const selectedContact = ref<ContactSearchResult | null>(null);
const contactSearchTerm = ref("");
const debouncedContactSearch = ref("");
const contactFilterAnchorRef = ref<HTMLElement | null>(null);
const contactResultsPosition = ref({ top: 0, left: 0, width: 0 });

// The results popover is teleported to <body> (see template) so it can escape the
// notifications panel's `overflow: hidden` (needed there to clip the header/footer
// corners) instead of being cut off by it. Position it under the search box using
// its live bounding rect.
const updateContactResultsPosition = () => {
  const anchor = contactFilterAnchorRef.value;
  if (!anchor) return;
  const rect = anchor.getBoundingClientRect();
  contactResultsPosition.value = {
    top: rect.bottom + 4,
    left: rect.left,
    width: rect.width,
  };
};

const debouncedSetContactSearch = debounce((val: string) => {
  debouncedContactSearch.value = val;
}, 300);

const resetContactSearch = () => {
  debouncedSetContactSearch.cancel();
  contactSearchTerm.value = "";
  debouncedContactSearch.value = "";
};

watch(contactSearchTerm, (val) => {
  if (!val || val.length < 2) {
    debouncedSetContactSearch.cancel();
    debouncedContactSearch.value = "";
    return;
  }
  debouncedSetContactSearch(val);
});

watch(debouncedContactSearch, (val) => {
  if (val.length >= 2) {
    nextTick(updateContactResultsPosition);
  }
});

const contactSearchParams = computed(() => {
  if (!props.account?.organizationId || debouncedContactSearch.value.length < 2) return null;
  return {
    organizationId: props.account.organizationId,
    searchTerm: debouncedContactSearch.value,
    limit: 8,
    page: 1,
  };
});

const { data: contactSearchData, isFetching: isSearchingContacts } =
  useContacts(contactSearchParams);

const contactSearchResults = computed<ContactSearchResult[]>(
  () => contactSearchData.value?.data || []
);

const getContactName = (c: ContactSearchResult | null): string => {
  if (!c) return "Unknown";
  return c.fullName || `${c.fname || ""} ${c.lname || ""}`.trim() || c.name || "Unknown";
};

const getContactInitials = (c: ContactSearchResult): string => {
  const name = getContactName(c);
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const selectContactFilter = (contact: ContactSearchResult) => {
  selectedContact.value = contact;
  resetContactSearch();
};

const clearContactFilter = () => {
  selectedContact.value = null;
};

// Filtering swaps in a different notification list — reset the "new notification"
// toast baseline so items that already existed under the new filter don't all fire
// toasts at once.
watch(selectedContact, () => {
  lastNotificationTimestamp.value = Date.now();
});

const notificationParams = computed(() => ({
  createdById: props.account?.id || null,
  organizationId: props.account?.organizationId || null,
  contactId: selectedContact.value?.id || null,
  limit: 15,
}));

const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
  useNotifications(notificationParams);

const { mutate: markAsSeen } = useMarkNotificationSeen();

const notifications = computed(() => {
  if (!data.value?.pages) return [];
  return data.value.pages.flatMap((page) => page?.data || []);
});

const unseenCount = computed(() => {
  return notifications.value.filter((n) => !n.isSeen).length;
});

const closeDropdown = () => {
  showDropdown.value = false;
  document.removeEventListener("click", handleClickOutside);
  resetContactSearch();
};

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
  if (showDropdown.value) {
    setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 0);
  } else {
    closeDropdown();
  }
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest(".notifications-dropdown")) {
    closeDropdown();
  }
};

const loadMore = () => {
  if (!isFetchingNextPage.value && hasNextPage.value) {
    fetchNextPage();
  }
};

const markNotificationsAsSeen = () => {
  const unseenIds = notifications.value.filter((n) => !n.isSeen).map((n) => n.id);
  if (unseenIds.length > 0) {
    markAsSeen(unseenIds);
  }
};

const handleNotificationClick = (notification: any) => {
  if (!notification.isSeen) {
    markAsSeen([notification.id]);
  }
};

const isLinkedInConnectionRequest = (notification: any): boolean => {
  const message = notification?.message?.toLowerCase() || "";
  return (
    notification?.type === NotificationCategory.account &&
    !!notification?.contact &&
    message.includes("linkedin connection request")
  );
};

const getNotificationIcon = (notification: any) => {
  const type = notification?.type;
  const eventType = notification?.meta?.eventType;

  if (type === NotificationCategory.task && eventType) {
    const eventIconMap: Record<string, string> = {
      linkedin_connection: "mdi:linkedin",
      email_opened: "mdi:email-open-outline",
      email_clicked: "mdi:cursor-default-click",
      inmail_read: "mdi:linkedin",
      linkedin_message_read: "mdi:linkedin",
      whatsapp_message_read: "mdi:whatsapp",
      sms_received: "mdi:message-text",
      call_conversation: "mdi:phone",
      linkedin_message: "mdi:linkedin",
      whatsapp_message: "mdi:whatsapp",
      email_received: "mdi:email",
    };
    if (eventIconMap[eventType]) {
      return eventIconMap[eventType];
    }
  }

  if (type === NotificationCategory.task) {
    const message = notification?.message?.toLowerCase() || "";
    if (message.includes("call with") && message.includes("follow-up")) {
      return "mdi:phone-message";
    }
    if (message.includes("tasks from") && message.includes("buckets")) {
      return "mdi:checkbox-marked-circle-outline";
    }
    return "mdi:alert-circle";
  }

  if (isLinkedInConnectionRequest(notification)) {
    return "mdi:linkedin";
  }

  const categoryIconMap: Record<string, string> = {
    [NotificationCategory.enrichment]: "mdi:database-sync",
    [NotificationCategory.meeting]: "mdi:calendar-clock",
    [NotificationCategory.account]: "mdi:account-alert",
    [NotificationCategory.mention]: "mdi:at",
  };
  return categoryIconMap[type] || "mdi:bell-outline";
};

const getNotificationTypeClass = (notification: any) => {
  const type = notification?.type;
  const eventType = notification?.meta?.eventType;

  if (type === NotificationCategory.task && eventType) {
    const eventClassMap: Record<string, string> = {
      linkedin_connection: "type-linkedin",
      email_opened: "type-email",
      email_clicked: "type-email",
      inmail_read: "type-linkedin",
      linkedin_message_read: "type-linkedin",
      whatsapp_message_read: "type-whatsapp",
      sms_received: "type-sms",
      call_conversation: "type-call",
      linkedin_message: "type-linkedin",
      whatsapp_message: "type-whatsapp",
      email_received: "type-email",
    };
    if (eventClassMap[eventType]) {
      return eventClassMap[eventType];
    }
  }

  if (type === NotificationCategory.task) {
    const message = notification?.message?.toLowerCase() || "";
    if (message.includes("call with") && message.includes("follow-up")) {
      return "type-call-followup";
    }
    return "type-task";
  }

  const message = notification?.message?.toLowerCase() || "";
  if (
    type === NotificationCategory.ai_generation ||
    message.includes("cue cards") ||
    message.includes("outreach cue")
  ) {
    return "type-ai";
  }

  if (isLinkedInConnectionRequest(notification)) {
    return "type-linkedin";
  }

  const classMap: Record<string, string> = {
    [NotificationCategory.enrichment]: "type-enrichment",
    [NotificationCategory.meeting]: "type-meeting",
    [NotificationCategory.account]: "type-account",
    [NotificationCategory.mention]: "type-mention",
  };
  return classMap[type] || "type-default";
};

const getNotificationTitle = (notification: any) => {
  if (!notification?.type) return "Notification";

  if (notification.type === NotificationCategory.enrichment) {
    return "Data Enrichment";
  }

  if (notification.type === NotificationCategory.ai_generation) {
    return "AI Outreach";
  }

  if (notification.type === NotificationCategory.meeting) {
    if (notification.meta?.eventType === "attendee_rsvp") {
      return "Meeting RSVP Update";
    }
    return "Meeting Reminder";
  }

  if (notification.type === NotificationCategory.mention) {
    return "Mentioned in Chat";
  }

  if (notification.type === NotificationCategory.account) {
    const message = notification.message?.toLowerCase() || "";
    if (isLinkedInConnectionRequest(notification)) {
      return "New LinkedIn Connection";
    }
    if (message.includes("deleted")) return "Account Deleted";
    if (message.includes("reconnect") || message.includes("disconnected"))
      return "Account Connection";
    return "Account Update";
  }

  if (notification.type === NotificationCategory.task) {
    const eventType = notification.meta?.eventType;

    if (eventType) {
      const eventTitleMap: Record<string, string> = {
        linkedin_message: "New LinkedIn Message",
        whatsapp_message: "New WhatsApp Message",
        email_received: "New Email",
        sms_received: "New SMS",
        call_conversation: "Call Completed",
        email_opened: "Email Opened",
        email_clicked: "Email Link Clicked",
        linkedin_connection: "Connection Accepted",
        inmail_read: "InMail Read",
        linkedin_message_read: "LinkedIn Message Read",
        whatsapp_message_read: "Message Read",
      };
      if (eventTitleMap[eventType]) {
        return eventTitleMap[eventType];
      }
    }

    const message = notification.message?.toLowerCase() || "";
    if (message.includes("tasks from") && message.includes("buckets")) return "Tasks Due";
    if (message.includes("call with")) return "Call Follow-up";
    return "Action Required";
  }

  return "Notification";
};

const buildLinkRoute = (linkUrl: string, notification?: any) => {
  const url = new URL(linkUrl, window.location.origin);
  const query: Record<string, string> = Object.fromEntries(url.searchParams);
  if (route.query.owner) query.owner = route.query.owner as string;

  const followUpDate = notification?.meta?.followUpDate;
  if (followUpDate && !query.status) {
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    if (followUpDate < todayKey) query.status = "past";
    else if (followUpDate === todayKey) query.status = "due";
    else query.status = "future";
  }

  return { path: url.pathname, query };
};

const getEnrichmentViewRoute = (notification: any) => {
  const contactIds = notification.meta?.contactIds;

  // Single contact — go to contact overview
  if (contactIds?.length === 1) {
    return { name: CONTACT_OVERVIEW_ROUTE, params: { id: contactIds[0] } };
  }

  // Multiple contacts — filter by exact IDs
  if (contactIds?.length > 1) {
    return { name: CONTACTS_ROUTE, query: { contactIds: contactIds.join(",") } };
  }

  // Fallback: filter by date
  const createdAt = new Date(notification.createdAt);
  if (isNaN(createdAt.getTime())) {
    return { name: CONTACTS_ROUTE };
  }
  const startOfDay = new Date(createdAt);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(createdAt);
  endOfDay.setHours(23, 59, 59, 999);

  return {
    name: CONTACTS_ROUTE,
    query: {
      startDate: startOfDay.toISOString(),
      endDate: endOfDay.toISOString(),
    },
  };
};

const showNotificationToast = (notification: any) => {
  const title = getNotificationTitle(notification);
  const message = notification.message;
  const contactName = notification.contact
    ? [notification.contact.fname, notification.contact.lname].filter(Boolean).join(" ") || null
    : null;
  const meetingTitle = notification.meeting?.title;

  let toastMessage = message;
  if (contactName) {
    toastMessage = `${title}: ${message} - ${contactName}`;
  } else if (meetingTitle) {
    toastMessage = `${title}: ${message} - ${meetingTitle}`;
  } else {
    toastMessage = `${title}: ${message}`;
  }

  const toastType =
    notification.type === NotificationCategory.task ||
    notification.type === NotificationCategory.account
      ? "warning"
      : "info";

  $toast?.[toastType](toastMessage, {
    duration: 5000,
  });
};

watch(showDropdown, (isOpen) => {
  if (isOpen) {
    markNotificationsAsSeen();
    window.addEventListener("resize", updateContactResultsPosition);
    window.addEventListener("scroll", updateContactResultsPosition, true);
  } else {
    window.removeEventListener("resize", updateContactResultsPosition);
    window.removeEventListener("scroll", updateContactResultsPosition, true);
  }
});

watch(
  notifications,
  (newNotifications, oldNotifications) => {
    if (!newNotifications || !Array.isArray(newNotifications)) return;

    if (!lastNotificationTimestamp.value && newNotifications.length > 0) {
      const latest = newNotifications[0];
      if (latest?.createdAt) {
        lastNotificationTimestamp.value = new Date(latest.createdAt).getTime();
      }
      return;
    }

    if (oldNotifications && Array.isArray(oldNotifications)) {
      newNotifications.forEach((notification) => {
        const notificationTime = new Date(notification.createdAt).getTime();
        const isNew = !oldNotifications.find((old) => old.id === notification.id);
        const isUnseen = !notification.isSeen;

        if (isNew && isUnseen && notificationTime > (lastNotificationTimestamp.value || 0)) {
          showNotificationToast(notification);
          lastNotificationTimestamp.value = Math.max(
            lastNotificationTimestamp.value || 0,
            notificationTime
          );
        }
      });
    }
  },
  { deep: true }
);

onMounted(() => {
  if (notifications.value.length > 0) {
    const latest = notifications.value[0];
    if (latest?.createdAt) {
      lastNotificationTimestamp.value = new Date(latest.createdAt).getTime();
    }
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  debouncedSetContactSearch.cancel();
  window.removeEventListener("resize", updateContactResultsPosition);
  window.removeEventListener("scroll", updateContactResultsPosition, true);
});
</script>

<style scoped>
.notification-bell-wrapper {
  display: inline-block;
}

.notification-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #ebeaff;
  border-radius: 50%;
  width: 33px;
  height: 33px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
}

.notification-wrapper:hover {
  background-color: #ddd9ff;
  transform: scale(1.05);
}

.notification-wrapper.has-new-notifications {
  animation: pulse-bell 2s ease-in-out infinite;
}

@keyframes pulse-bell {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.notification-icon {
  position: relative;
  color: #4c3dff;
  font-size: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background-color: #ef4444;
  color: white;
  font-size: 10px;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  padding: 0 4px;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}

.notifications-dropdown {
  position: absolute;
  top: 45px;
  right: 0;
  width: 420px;
  max-height: 500px;
  background: #fff;
  border: 1px solid #e6e8ec;
  border-radius: 12px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 10080;
  display: flex;
  flex-direction: column;
  animation: slideDown 0.3s ease-out;
  overflow: hidden;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.notifications-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e6e8ec;
  background: #fafafa;
}

.notifications-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #18181b;
}

.unread-count-badge {
  background-color: #3e3efe;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  min-width: 24px;
  text-align: center;
}

.notifications-filter {
  position: relative;
  padding: 10px 20px;
  border-bottom: 1px solid #e6e8ec;
  background: #fff;
}

.contact-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ebeaff;
  color: #3e3efe;
  border-radius: 16px;
  padding: 4px 8px 4px 4px;
  font-size: 0.8125rem;
  font-weight: 500;
}

.contact-filter-chip__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ddd9ff;
  color: #3e3efe;
  flex-shrink: 0;
  overflow: hidden;
}

.contact-filter-chip__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.contact-filter-chip__name {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-filter-chip__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #3e3efe;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
}

.contact-filter-chip__clear:hover {
  background: #ddd9ff;
}

.contact-filter-search-wrapper {
  position: relative;
}

.contact-filter-search {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 10px;
}

.contact-filter-search__icon {
  color: #9ca3af;
  flex-shrink: 0;
}

.contact-filter-search__input {
  flex: 1;
  border: none;
  background: none;
  outline: none;
  font-size: 0.8125rem;
  color: #18181b;
  min-width: 0;
}

.contact-filter-search__input::placeholder {
  color: #9ca3af;
}

.contact-filter-search__spinner {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border: 2px solid #d1d5db;
  border-top-color: #3e3efe;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.contact-filter-results {
  position: fixed;
  background: #fff;
  border: 1px solid #e6e8ec;
  border-radius: 10px;
  box-shadow: 0px 10px 28px rgba(0, 0, 0, 0.16);
  max-height: 260px;
  overflow-y: auto;
  z-index: 10090;
  animation: contactResultsFadeIn 0.15s ease-out;
}

@keyframes contactResultsFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.contact-filter-empty {
  padding: 16px 12px;
  font-size: 0.8125rem;
  color: #9ca3af;
  text-align: center;
}

.contact-filter-result-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  background: none;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  text-align: left;
}

.contact-filter-result-item:last-child {
  border-bottom: none;
}

.contact-filter-result-item:hover {
  background: #f9fafb;
}

.contact-filter-result-item__avatar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ebeaff;
  color: #3e3efe;
  font-size: 0.75rem;
  font-weight: 600;
  overflow: hidden;
}

.contact-filter-result-item__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.contact-filter-result-item__info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.contact-filter-result-item__name {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #18181b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-filter-result-item__meta {
  font-size: 0.75rem;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notifications-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 120px;
}

.notifications-list {
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
}

.notifications-list::-webkit-scrollbar {
  width: 6px;
}

.notifications-list::-webkit-scrollbar-track {
  background: transparent;
}

.notifications-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.notifications-list::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.notification-item {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.notification-item:hover {
  background: #f9fafb;
}

.notification-item.unread {
  background: #f0f7ff;
}

.notification-item.unread:hover {
  background: #e0efff;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-icon-wrapper {
  position: relative;
  flex-shrink: 0;
}

.notification-type-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
}

.notification-type-icon.type-enrichment {
  background: #eff6ff;
  color: #3b82f6;
  box-shadow: 0 1px 3px rgba(59, 130, 246, 0.1);
}

.notification-type-icon.type-meeting {
  background: #fffbeb;
  color: #f59e0b;
  box-shadow: 0 1px 3px rgba(245, 158, 11, 0.1);
}

.notification-type-icon.type-task {
  background: #fef2f2;
  color: #ef4444;
  box-shadow: 0 1px 3px rgba(239, 68, 68, 0.1);
}

.notification-type-icon.type-account {
  background: #fef3c7;
  color: #d97706;
  box-shadow: 0 1px 3px rgba(217, 119, 6, 0.1);
}

.notification-type-icon.type-linkedin {
  background: #e7f3ff;
  color: #0a66c2;
  box-shadow: 0 1px 3px rgba(10, 102, 194, 0.1);
}

.notification-type-icon.type-email {
  background: #fef2f2;
  color: #ef4444;
  box-shadow: 0 1px 3px rgba(239, 68, 68, 0.1);
}

.notification-type-icon.type-whatsapp {
  background: #dcfce7;
  color: #25d366;
  box-shadow: 0 1px 3px rgba(37, 211, 102, 0.1);
}

.notification-type-icon.type-sms {
  background: #f3e8ff;
  color: #8b5cf6;
  box-shadow: 0 1px 3px rgba(139, 92, 246, 0.1);
}

.notification-type-icon.type-call {
  background: #dcfce7;
  color: #22c55e;
  box-shadow: 0 1px 3px rgba(34, 197, 94, 0.1);
}

.notification-type-icon.type-call-followup {
  background: #fffbeb;
  color: #f59e0b;
  box-shadow: 0 1px 3px rgba(245, 158, 11, 0.1);
}

.notification-type-icon.type-ai {
  background: #fff4e6;
  color: #f97316;
  box-shadow: 0 1px 3px rgba(249, 115, 22, 0.15);
}

.notification-type-icon.type-mention {
  background: #eef2ff;
  color: #6366f1;
  box-shadow: 0 1px 3px rgba(99, 102, 241, 0.15);
}

.notification-type-icon.type-default {
  background: #f9fafb;
  color: #6b7280;
  box-shadow: 0 1px 3px rgba(107, 114, 128, 0.1);
}

.unread-indicator {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  background-color: #3e3efe;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-message {
  margin-bottom: 8px;
}

.notification-title {
  margin: 0 0 4px 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.notification-title.unread-text {
  color: #3e3efe;
  font-weight: 700;
}

.notification-message-text {
  margin: 0 0 6px 0;
  font-size: 0.875rem;
  color: #18181b;
  line-height: 1.5;
}

.notification-message-text.unread-text {
  font-weight: 500;
  color: #111827;
}

.notification-entity {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 0.8125rem;
  color: #6b7280;
}

.notification-entity .iconify {
  color: #9ca3af;
}

.notification-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.notification-time {
  font-size: 0.75rem;
  color: #9ca3af;
}

.notification-actions {
  display: flex;
  gap: 8px;
}

.notification-action-link {
  color: #3e3efe;
  font-size: 0.8125rem;
  font-weight: 500;
  text-decoration: none;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  background: none;
  border: none;
  cursor: pointer;
}

.notification-action-link:hover {
  background: #ebeaff;
  color: #2d2dfe;
}

.no-notifications {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #9ca3af;
  padding: 40px 20px;
}

.no-notifications .iconify {
  color: #d1d5db;
  margin-bottom: 12px;
}

.no-notifications p {
  margin: 0;
  font-size: 0.875rem;
}

.load-more-wrapper {
  padding: 12px 16px 14px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

.load-more-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  background: #fff;
  border: 1px solid #e5e7eb;
  padding: 10px 16px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #3e3efe;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
}

.load-more-btn:hover:not(:disabled) {
  background: #ebeaff;
  border-color: #c7c7ff;
  color: #2d2dfe;
}

.load-more-btn:disabled {
  cursor: not-allowed;
  color: #9ca3af;
  background: #f9fafb;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
